import React, { useState, useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import {
  Sparkles,
  X,
  Send,
  RotateCcw,
  Check,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const STORAGE_KEY = "tracker:agent:v1";

const newSessionId = () =>
  `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const loadState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { sessionId: newSessionId(), messages: [] };
    const parsed = JSON.parse(raw);
    return {
      sessionId: parsed.sessionId || newSessionId(),
      messages: parsed.messages || [],
    };
  } catch {
    return { sessionId: newSessionId(), messages: [] };
  }
};

// Strip ```action ... ``` blocks from displayed text
const cleanText = (raw) =>
  (raw || "")
    .replace(/```action\s*\n[\s\S]*?\n```/g, "")
    .trim();

// Tiny markdown renderer: bold, lists, line breaks
const Markdown = ({ text }) => {
  const html = (text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 py-0.5 rounded text-[12px]">$1</code>')
    .replace(/^- (.+)$/gm, '<li class="ml-4 list-disc">$1</li>')
    .replace(/\n/g, "<br/>");
  return <div className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />;
};

const ActionCard = ({ action, onConfirm, onCancel, busy, done }) => {
  const isSync = action.type === "sync_zoho";
  const isCost = action.type === "add_cost";
  return (
    <div
      className="mt-3 border border-amber-300 bg-amber-50/70 rounded-md p-3"
      data-testid="agent-action-card"
    >
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-amber-900 uppercase tracking-wider">
            {isSync && "Confirm: Sync Zoho"}
            {isCost && "Confirm: Add cost entry"}
            {!isSync && !isCost && `Confirm: ${action.type}`}
          </div>
          {isCost && (
            <div className="text-xs text-amber-900 mt-1 space-y-0.5">
              <div><strong>Head:</strong> {action.head}</div>
              <div><strong>Amount:</strong> {action.currency || "USD"} {Number(action.amount).toLocaleString()}</div>
              {action.date && <div><strong>Date:</strong> {action.date}</div>}
              {action.period_month && <div><strong>Month:</strong> {action.period_month}</div>}
              {(action.note || action.notes) && <div><strong>Note:</strong> {action.note || action.notes}</div>}
            </div>
          )}
          {isSync && (
            <div className="text-xs text-amber-900 mt-1">{action.summary || "Pull latest data from Zoho Books"}</div>
          )}
          {done ? (
            <div className="text-xs text-emerald-700 font-medium mt-2 flex items-center gap-1">
              <Check className="w-3.5 h-3.5" /> Executed
            </div>
          ) : (
            <div className="flex gap-2 mt-3">
              <button
                onClick={onConfirm}
                disabled={busy}
                data-testid="agent-action-confirm"
                className="bg-[#1A2332] text-white text-xs px-3 py-1.5 rounded font-medium hover:bg-[#243042] flex items-center gap-1 disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                Confirm
              </button>
              <button
                onClick={onCancel}
                disabled={busy}
                data-testid="agent-action-cancel"
                className="border border-gray-300 text-gray-700 text-xs px-3 py-1.5 rounded font-medium hover:bg-gray-100"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AgentChat = () => {
  const [open, setOpen] = useState(false);
  const [{ sessionId, messages }, setState] = useState(loadState);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  // persist
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ sessionId, messages }));
    } catch {
      /* ignore */
    }
  }, [sessionId, messages]);

  // auto-scroll
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  // focus input on open
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  const send = useCallback(
    async (text, opts = {}) => {
      const msg = (text ?? input).trim();
      if (!msg || loading) return;
      setInput("");
      setLoading(true);
      const userMsg = { id: `m_${Date.now()}_u`, role: "user", text: msg, ts: Date.now() };
      setState((s) => ({ ...s, messages: [...s.messages, userMsg] }));
      try {
        const r = await api.post("/agent/chat", {
          session_id: sessionId,
          message: msg,
          refresh: !!opts.refresh,
        });
        const asstMsg = {
          id: `m_${Date.now()}_a`,
          role: "assistant",
          text: r.data.reply || "",
          actions: r.data.actions || [],
          actionStatus: {}, // index -> 'done' | undefined
          ts: Date.now(),
        };
        setState((s) => ({ ...s, messages: [...s.messages, asstMsg] }));
      } catch (e) {
        const errText =
          e.response?.status === 402
            ? "⚠️ AI service is temporarily unavailable. Please try again later or contact support."
            : e.response?.data?.detail || "AI agent error";
        setState((s) => ({
          ...s,
          messages: [
            ...s.messages,
            { id: `m_${Date.now()}_e`, role: "error", text: errText, ts: Date.now() },
          ],
        }));
      } finally {
        setLoading(false);
      }
    },
    [input, loading, sessionId]
  );

  const reset = () => {
    const fresh = newSessionId();
    setState({ sessionId: fresh, messages: [] });
    toast.success("Started a fresh chat with refreshed data");
  };

  const executeAction = async (messageId, actionIdx, action) => {
    setActionBusy(`${messageId}:${actionIdx}`);
    try {
      const r = await api.post("/agent/execute", {
        session_id: sessionId,
        action,
      });
      setState((s) => ({
        ...s,
        messages: s.messages.map((m) =>
          m.id === messageId
            ? { ...m, actionStatus: { ...(m.actionStatus || {}), [actionIdx]: "done" } }
            : m
        ),
      }));
      if (action.type === "sync_zoho") {
        toast.success(`Synced: ${r.data.result?.counts?.invoices ?? "?"} invoices`);
      } else if (action.type === "add_cost") {
        toast.success(`Cost added: ${r.data.result?.head} · $${r.data.result?.amount}`);
      } else {
        toast.success("Action executed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setActionBusy(null);
    }
  };

  const suggestions = [
    "What is my net margin YTD?",
    "List unpaid invoices over $50K",
    "How much have I spent on Partner Commissions?",
    "Sync Zoho now",
  ];

  return (
    <>
      {/* Floating launcher */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="launcher"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            onClick={() => setOpen(true)}
            data-testid="agent-launcher"
            className="fixed bottom-6 right-6 z-[60] flex items-center gap-2 px-4 py-3 rounded-full shadow-lg text-white font-medium text-sm hover:shadow-xl transition-shadow"
            style={{
              background: "linear-gradient(135deg, #1A2332 0%, #408DFB 100%)",
            }}
            aria-label="Open AI agent"
          >
            <Sparkles className="w-4 h-4" />
            <span className="hidden sm:inline">Ask Tracker AI</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            data-testid="agent-panel"
            className="fixed bottom-6 right-6 z-[60] w-[min(420px,calc(100vw-2rem))] h-[min(640px,calc(100vh-3rem))] bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div
              className="px-4 py-3 flex items-center justify-between text-white shrink-0"
              style={{ background: "linear-gradient(135deg, #1A2332 0%, #243042 100%)" }}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-amber-300" />
                </div>
                <div>
                  <div className="text-sm font-semibold leading-none">Tracker AI</div>
                  <div className="text-[10px] text-white/60 mt-1 uppercase tracking-wider">
                    Live data · DigiTrans
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={reset}
                  data-testid="agent-reset"
                  className="p-1.5 rounded hover:bg-white/10 transition-colors"
                  title="Reset chat (refresh data)"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setOpen(false)}
                  data-testid="agent-close"
                  className="p-1.5 rounded hover:bg-white/10 transition-colors"
                  title="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div
              ref={listRef}
              className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAFAFA]"
              data-testid="agent-messages"
            >
              {messages.length === 0 && (
                <div className="text-center py-6">
                  <div className="text-sm text-gray-600 mb-3">
                    Ask me anything about your contracts, invoices, costs or margins.
                  </div>
                  <div className="flex flex-col gap-2">
                    {suggestions.map((s) => (
                      <button
                        key={s}
                        onClick={() => send(s)}
                        data-testid={`agent-suggestion-${s.slice(0, 12).replace(/\s/g, "-")}`}
                        className="text-left text-xs text-gray-700 bg-white border border-gray-200 hover:border-[#408DFB] hover:text-[#1A2332] rounded-md px-3 py-2 transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m) => {
                if (m.role === "user") {
                  return (
                    <div key={m.id} className="flex justify-end">
                      <div
                        data-testid="agent-msg-user"
                        className="max-w-[85%] bg-[#1A2332] text-white text-sm px-3.5 py-2 rounded-lg rounded-br-sm"
                      >
                        {m.text}
                      </div>
                    </div>
                  );
                }
                if (m.role === "error") {
                  return (
                    <div key={m.id} className="flex justify-start">
                      <div
                        data-testid="agent-msg-error"
                        className="max-w-[90%] bg-red-50 border border-red-200 text-red-800 text-xs px-3 py-2 rounded-lg"
                      >
                        {m.text}
                      </div>
                    </div>
                  );
                }
                // assistant
                const display = cleanText(m.text);
                return (
                  <div key={m.id} className="flex justify-start">
                    <div
                      data-testid="agent-msg-assistant"
                      className="max-w-[90%] bg-white border border-gray-200 text-gray-900 px-3.5 py-2.5 rounded-lg rounded-bl-sm shadow-sm"
                    >
                      {display && <Markdown text={display} />}
                      {(m.actions || []).map((a, idx) => (
                        <ActionCard
                          key={idx}
                          action={a}
                          busy={actionBusy === `${m.id}:${idx}`}
                          done={(m.actionStatus || {})[idx] === "done"}
                          onConfirm={() => executeAction(m.id, idx, a)}
                          onCancel={() =>
                            setState((s) => ({
                              ...s,
                              messages: s.messages.map((x) =>
                                x.id === m.id
                                  ? {
                                      ...x,
                                      actionStatus: {
                                        ...(x.actionStatus || {}),
                                        [idx]: "cancelled",
                                      },
                                    }
                                  : x
                              ),
                            }))
                          }
                        />
                      ))}
                    </div>
                  </div>
                );
              })}

              {loading && (
                <div className="flex justify-start" data-testid="agent-typing">
                  <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-1.5 shadow-sm">
                    <motion.span
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 1, repeat: Infinity }}
                      className="w-1.5 h-1.5 rounded-full bg-[#408DFB]"
                    />
                    <motion.span
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
                      className="w-1.5 h-1.5 rounded-full bg-[#408DFB]"
                    />
                    <motion.span
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
                      className="w-1.5 h-1.5 rounded-full bg-[#408DFB]"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="border-t border-gray-200 p-2.5 flex items-center gap-2 bg-white shrink-0"
              data-testid="agent-form"
            >
              <input
                ref={inputRef}
                type="text"
                placeholder="Ask about contracts, invoices, costs..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={loading}
                data-testid="agent-input"
                className="flex-1 text-sm bg-gray-50 border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:border-[#408DFB] focus:bg-white transition-colors disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={!input.trim() || loading}
                data-testid="agent-send"
                className="bg-[#1A2332] text-white p-2 rounded-md hover:bg-[#243042] transition-colors disabled:opacity-50"
                aria-label="Send"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AgentChat;
